import { FiChevronRight, FiLock } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/formatters";
import { parseDateLocal } from "@/utils/dateUtils";
import type { WrappedListItem } from "../types";

interface WrappedHistoryListProps {
  reports: WrappedListItem[];
  isLoading?: boolean;
  isError?: boolean;
  onOpenPeriod: (period: string) => void;
}

export const WrappedHistoryList = ({ reports, isLoading = false, isError = false, onOpenPeriod }: WrappedHistoryListProps) => {
  const { t } = useTranslation("wrapped");
  const sortedReports = [...reports].sort((first, second) => second.period_start.localeCompare(first.period_start));

  return (
    <section className="app-panel-raised min-w-0 p-5 sm:p-6" aria-labelledby="wrapped-history-title">
      <div className="flex items-baseline justify-between gap-4">
        <h2 id="wrapped-history-title" className="text-[length:var(--ds-type-heading-size)] font-medium leading-[var(--ds-type-heading-line)] tracking-[var(--ds-type-heading-tracking)] text-[var(--app-text)]">{t("wrapped.historyTitle")}</h2>
        {sortedReports.length > 0 ? <span className="text-xs text-[var(--app-muted-text)]">{sortedReports.length}</span> : null}
      </div>

      {isLoading ? <p className="mt-5 text-sm text-[var(--app-text-secondary)]" role="status">{t("wrapped.loading")}</p> : null}
      {isError ? <p className="mt-5 text-sm text-[var(--app-danger-text)]" role="alert">{t("wrapped.loadError")}</p> : null}
      {!isLoading && !isError && sortedReports.length === 0 ? (
        <p className="mt-5 text-sm leading-5 text-[var(--app-text-secondary)]" role="status">{t("wrapped.historyEmpty")}</p>
      ) : null}

      {!isLoading && !isError && sortedReports.length > 0 ? (
        <ul className="mt-4 divide-y divide-[color:color-mix(in_srgb,var(--app-border)_72%,transparent)]">
          {sortedReports.map((report) => {
            const periodLabel = formatDate(parseDateLocal(report.period_start), undefined, { month: "long", year: "numeric" }) || report.period;
            const isReady = report.status === "READY";
            const isUnread = report.first_viewed_at === null;
            const content = (
              <>
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 break-words font-semibold text-[var(--app-text)]">{periodLabel}</span>
                    {isUnread ? (
                      <span className="shrink-0 rounded-full bg-[var(--app-accent-soft)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--app-accent)]">
                        {t("wrapped.unread")}
                      </span>
                    ) : null}
                  </span>
                  {report.title ? <span className="mt-1 block break-words text-sm text-[var(--app-text-secondary)]">{report.title}</span> : null}
                </span>
                {isReady ? <FiChevronRight className="shrink-0 text-[var(--app-muted-text)]" size={18} aria-hidden="true" /> : <FiLock className="shrink-0 text-[var(--app-muted-text)]" size={16} aria-hidden="true" />}
              </>
            );

            return (
              <li key={report.id}>
                {isReady ? (
                  <button
                    type="button"
                    className="app-focus-ring flex min-h-16 w-full min-w-0 items-center gap-3 px-1 py-3 text-left transition hover:bg-[var(--app-row-hover-bg)]"
                    onClick={() => onOpenPeriod(report.period)}
                    aria-label={t("wrapped.openPeriod", { period: periodLabel })}
                  >
                    {content}
                  </button>
                ) : (
                  <div className="flex min-h-16 min-w-0 items-center gap-3 px-1 py-3" aria-disabled="true">
                    {content}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
};
